import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientProfile from "@/pages/PatientProfile";
import TodaysClinic from "@/pages/TodaysClinic";
import AbsentPatients from "@/pages/AbsentPatients";
import PresentPatients from "@/pages/PresentPatients";
import Payments from "@/pages/Payments";
import FollowUps from "@/pages/FollowUps";
import ReviewRequests from "@/pages/ReviewRequests";
import Appointments from "@/pages/Appointments";
import HomeVisits from "@/pages/HomeVisits";
import HomeDashboard from "@/pages/HomeDashboard";
import Deposits from "@/pages/Deposits";
import Collections from "@/pages/Collections";
import Expenses from "@/pages/Expenses";
import Settlements from "@/pages/Settlements";
import PhysioSettlement from "@/pages/PhysioSettlement";
import Staff from "@/pages/Staff";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import DataQuality from "@/pages/DataQuality";
import AuditLogs from "@/pages/AuditLogs";
import Profile from "@/pages/Profile";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <img src="/logo.png" alt="BK" className="h-14 w-14 rounded-xl animate-pulse" />
        <Loader2 className="h-5 w-5 animate-spin text-bk-green" />
      </div>
    </div>
  );
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

const R = { admin: "admin", rec: "receptionist", physio: "physiotherapist" };

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/patients" element={<Protected><Patients /></Protected>} />
      <Route path="/patients/:id" element={<Protected><PatientProfile /></Protected>} />
      <Route path="/clinic" element={<Protected roles={[R.admin, R.rec]}><TodaysClinic /></Protected>} />
      <Route path="/present" element={<Protected roles={[R.admin, R.rec]}><PresentPatients /></Protected>} />
      <Route path="/absent" element={<Protected roles={[R.admin, R.rec]}><AbsentPatients /></Protected>} />
      <Route path="/payments" element={<Protected roles={[R.admin, R.rec]}><Payments /></Protected>} />
      <Route path="/follow-ups" element={<Protected roles={[R.admin, R.rec]}><FollowUps /></Protected>} />
      <Route path="/reviews" element={<Protected roles={[R.admin, R.rec]}><ReviewRequests /></Protected>} />
      <Route path="/appointments" element={<Protected roles={[R.admin, R.rec]}><Appointments /></Protected>} />
      <Route path="/home-dashboard" element={<Protected><HomeDashboard /></Protected>} />
      <Route path="/home-visits" element={<Protected roles={[R.admin, R.physio]}><HomeVisits /></Protected>} />
      <Route path="/deposits" element={<Protected roles={[R.admin]}><Deposits /></Protected>} />
      <Route path="/collections" element={<Protected roles={[R.physio]}><Collections /></Protected>} />
      <Route path="/expenses" element={<Protected roles={[R.admin, R.physio]}><Expenses /></Protected>} />
      <Route path="/settlements" element={<Protected roles={[R.admin]}><Settlements /></Protected>} />
      <Route path="/settlement" element={<Protected roles={[R.physio]}><PhysioSettlement /></Protected>} />
      <Route path="/staff" element={<Protected roles={[R.admin]}><Staff /></Protected>} />
      <Route path="/reports" element={<Protected roles={[R.admin]}><Reports /></Protected>} />
      <Route path="/data-quality" element={<Protected roles={[R.admin]}><DataQuality /></Protected>} />
      <Route path="/audit" element={<Protected roles={[R.admin]}><AuditLogs /></Protected>} />
      <Route path="/settings" element={<Protected roles={[R.admin]}><Settings /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
